/**
 * /assets/requisitions — the custody worklist (ADR-0024 Phase 4).
 *
 * D2 IS ENFORCED IN THE UI: approving the NEED (the manager) and issuing the
 * SERIAL (the custodian) are DISTINCT actions in distinct tabs — never one
 * button, even for a user who holds both roles. Collapsing them would destroy
 * the custodian control the API still enforces.
 *
 * Tabs: Pending approval (requested → approve/reject) · Awaiting issue
 * (approved → bind a serial) · Issued (return / transfer / handover docs).
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import {
  listAssignments, requestAsset, approveRequest, rejectRequest, issueAsset, returnAsset, transferAsset,
  listAssets, listCategories, listAttachments, uploadAttachment, downloadAttachment,
  HANDOVER_ACCEPT, HANDOVER_ALLOWED_LABEL, listRoster, type Assignment,
} from '../api/assets.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { AssignmentStatusBadge, TabButton, fmtAssignedAt } from './assetShared.js';

export const assetWorklistRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/assets/requisitions',
  component: AssetWorklistPage,
});

const TABS = [
  { label: 'Pending approval', status: 'requested' as const },
  { label: 'Awaiting issue', status: 'approved' as const },
  { label: 'Issued', status: 'issued' as const },
];

function AssetWorklistPage(): JSX.Element {
  const qc = useQueryClient();
  const [tabIdx, setTabIdx] = useState(0);
  const tab = TABS[tabIdx]!;
  const query = useQuery({ queryKey: ['asset', 'worklist', tab.status], queryFn: () => listAssignments({ status: tab.status }) });
  const rows = query.data?.assignments ?? [];
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['asset', 'worklist'] });

  return (
    <section className="p-8 space-y-6">
      <header className="space-y-1">
        <h1 className="font-serif text-3xl">Asset requisitions</h1>
        <p className="text-sm text-gray-400">Raise, approve, and fulfil custody. Approving the need and issuing a serial are separate steps.</p>
      </header>

      <RaiseRequisition onRaised={invalidate} />

      <div className="flex gap-1.5">
        {TABS.map((t, i) => <TabButton key={t.status} active={i === tabIdx} onClick={() => setTabIdx(i)}>{t.label}</TabButton>)}
      </div>

      {query.isLoading ? <p className="text-sm text-gray-400">Loading…</p> : null}
      <ul className="space-y-2">
        {rows.map((a) => <WorklistRow key={a.id} a={a} onChanged={invalidate} />)}
        {rows.length === 0 && !query.isLoading ? <li className="text-sm text-gray-500">Nothing here.</li> : null}
      </ul>
    </section>
  );
}

function personLabel(a: Assignment): string {
  return a.employee ? `${a.employee.firstName} ${a.employee.lastName} (${a.employee.employeeNumber})` : a.employeeId;
}

function WorklistRow({ a, onChanged }: { a: Assignment; onChanged: () => void }): JSX.Element {
  return (
    <li className="rounded border border-gray-900 px-4 py-3 text-sm space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-0.5">
          <div className="text-gray-200">{personLabel(a)}</div>
          <div className="text-xs text-gray-500">{a.category?.label ?? a.categoryId}{a.asset ? ` · ${a.asset.assetTag} (${a.asset.name})` : ''}</div>
          {a.status === 'issued' ? <div className="text-xs text-gray-500">Received: {fmtAssignedAt(a.assignedAt)}</div> : null}
        </div>
        <AssignmentStatusBadge status={a.status} />
      </div>
      {a.status === 'requested' ? <ApproveReject a={a} onChanged={onChanged} /> : null}
      {a.status === 'approved' ? <IssueControl a={a} onChanged={onChanged} /> : null}
      {a.status === 'issued' ? <IssuedControls a={a} onChanged={onChanged} /> : null}
    </li>
  );
}

/** D2 — the APPROVER control (the need). Distinct from issue. */
function ApproveReject({ a, onChanged }: { a: Assignment; onChanged: () => void }): JSX.Element {
  const approve = useMutation({ mutationFn: () => approveRequest(a.id), onSuccess: onChanged });
  const reject = useMutation({ mutationFn: () => rejectRequest(a.id), onSuccess: onChanged });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" onClick={() => approve.mutate()} disabled={approve.isPending} className="rounded bg-white text-black px-3 py-1.5 text-xs font-medium disabled:opacity-50">Approve need</button>
      <button type="button" onClick={() => reject.mutate()} disabled={reject.isPending} className="rounded border border-gray-700 text-gray-300 px-3 py-1.5 text-xs">Reject</button>
      <ApiErrorNotice error={approve.error ?? reject.error} fallback="Action failed." compact />
    </div>
  );
}

/** D2 — the CUSTODIAN control (bind a serial). A SEPARATE step from approval;
 *  the custodian picks a specific available asset of the requested category. */
function IssueControl({ a, onChanged }: { a: Assignment; onChanged: () => void }): JSX.Element {
  const [assetId, setAssetId] = useState('');
  const available = useQuery({ queryKey: ['asset', 'available', a.categoryId], queryFn: () => listAssets({ categoryId: a.categoryId, status: 'available' }) });
  const options = available.data?.assets ?? [];
  const issue = useMutation({ mutationFn: () => issueAsset(a.id, { assetId }), onSuccess: onChanged });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select value={assetId} onChange={(e) => setAssetId(e.target.value)} aria-label="Bind a serial" className="rounded border border-gray-700 bg-gray-900 px-2 py-1.5 text-white text-xs">
        <option value="">Choose an available asset…</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.assetTag} — {o.name}</option>)}
      </select>
      <button type="button" onClick={() => issue.mutate()} disabled={!assetId || issue.isPending} className="rounded bg-white text-black px-3 py-1.5 text-xs font-medium disabled:opacity-50">Issue asset</button>
      {options.length === 0 ? <span className="text-xs text-gray-500">No available assets in this category.</span> : null}
      <ApiErrorNotice error={issue.error} fallback="Action failed." compact />
    </div>
  );
}

function IssuedControls({ a, onChanged }: { a: Assignment; onChanged: () => void }): JSX.Element {
  const ret = useMutation({ mutationFn: () => returnAsset(a.id), onSuccess: onChanged });
  const [toEmp, setToEmp] = useState('');
  const emps = useQuery({ queryKey: ['employees', 'picker'], queryFn: () => listRoster({ status: 'active' }) });
  const transfer = useMutation({ mutationFn: () => transferAsset(a.assetId!, { toEmployeeId: toEmp }), onSuccess: onChanged });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => ret.mutate()} disabled={ret.isPending} className="rounded border border-gray-700 text-gray-300 px-3 py-1.5 text-xs">Mark returned</button>
        <select value={toEmp} onChange={(e) => setToEmp(e.target.value)} aria-label="Transfer to" className="rounded border border-gray-700 bg-gray-900 px-2 py-1.5 text-white text-xs">
          <option value="">Transfer to…</option>
          {(emps.data?.employees ?? []).filter((e) => e.id !== a.employeeId).map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>)}
        </select>
        <button type="button" onClick={() => transfer.mutate()} disabled={!toEmp || transfer.isPending} className="rounded border border-gray-700 text-gray-300 px-3 py-1.5 text-xs">Transfer</button>
        <ApiErrorNotice error={ret.error ?? transfer.error} fallback="Action failed." compact />
      </div>
      <Attachments assignmentId={a.id} />
    </div>
  );
}

function Attachments({ assignmentId }: { assignmentId: string }): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['asset', 'attachments', assignmentId], queryFn: () => listAttachments(assignmentId) });
  const docs = query.data?.attachments ?? [];
  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(assignmentId, file),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['asset', 'attachments', assignmentId] }),
  });
  return (
    <div className="rounded border border-gray-900 p-2 space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Handover docs:</span>
        <label className="text-xs text-blue-300 underline cursor-pointer">
          Upload
          <input type="file" accept={HANDOVER_ACCEPT} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); }} />
        </label>
        <span className="text-[11px] text-gray-600">({HANDOVER_ALLOWED_LABEL})</span>
      </div>
      {docs.length > 0 ? (
        <ul className="text-xs text-gray-400 space-y-0.5">
          {docs.map((d) => <li key={d.id}><button type="button" className="underline" onClick={() => void downloadAttachment(assignmentId, d.id, d.filename)}>{d.filename}</button>{d.label ? ` — ${d.label}` : ''}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

function RaiseRequisition({ onRaised }: { onRaised: () => void }): JSX.Element {
  const [employeeId, setEmployeeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const emps = useQuery({ queryKey: ['employees', 'picker'], queryFn: () => listRoster({ status: 'active' }) });
  const cats = useQuery({ queryKey: ['asset', 'categories'], queryFn: listCategories });
  const raise = useMutation({
    mutationFn: () => requestAsset({ employeeId, categoryId }),
    onSuccess: () => { setEmployeeId(''); setCategoryId(''); onRaised(); },
  });
  return (
    <form className="rounded border border-gray-900 p-4 flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); if (employeeId && categoryId) raise.mutate(); }}>
      <label className="text-sm text-gray-300 space-y-1"><span>For employee</span>
        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} aria-label="For employee" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm">
          <option value="">Select…</option>
          {(emps.data?.employees ?? []).map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>)}
        </select>
      </label>
      <label className="text-sm text-gray-300 space-y-1"><span>Category</span>
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Requisition category" className="block rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm">
          <option value="">Select…</option>
          {(cats.data?.categories ?? []).filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </label>
      <button type="submit" disabled={!employeeId || !categoryId || raise.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium disabled:opacity-50">Raise requisition</button>
      <ApiErrorNotice error={raise.error} fallback="Could not raise the requisition." className="w-full" />
    </form>
  );
}
